import { untrack } from 'svelte'

export type SettledWatchCallback<Value> = (current: Value, previous: Value) => void

/**
 * Observes values after Svelte has coalesced reactive writes for the current flush.
 * The first observation establishes the baseline without invoking the callback.
 * Use StateTransitionNotifier when every explicit write-boundary transition must be delivered.
 */
export function watchSettled<Value>(
    getValue: () => Value,
    callback: SettledWatchCallback<Value>
): () => void {
    let previousObservation: { value: Value } | undefined

    // Author note: this effect invokes an external callback in response to settled reactive state.
    // A derived value cannot perform that side effect, and event, binding, attachment, and mount
    // alternatives do not apply to a service-owned reactive source. The callback is untracked so
    // only getValue controls invalidation, and the returned root teardown makes ownership explicit.
    return $effect.root(() => {
        $effect(() => {
            const current = getValue()
            const previous = previousObservation
            previousObservation = { value: current }

            if (previous === undefined) {
                return
            }

            untrack(() => callback(current, previous.value))
        })
    })
}
